import * as yup from "yup";
import { StringHelper } from "../../assets/utils/helpers";
import { Constants } from "../../assets/utils/lib/constants";
import Drawer from "../../assets/vfx/drawer/drawer";
import Form from "../../assets/vfx/form/form";
import { Module } from "../../assets/types";
import { CommunityDto } from "../../services/dto";
import { SettingTypeDto } from "./settingContainer";
import { DropdownOption } from "../../assets/vfx/controls/components/dropdown/controlDropdown";
import { DrawerFormSaveResult, hasClientErrorOperationStatusCode } from "../../assets/utils/lib";

interface SettingFormProps<T> {
  loading?: boolean;
  data?: T | undefined;
  show: boolean;
  moduleType: Module;
  communities?: CommunityDto[];
  onHide: () => void;
  onSave: (values: any) => DrawerFormSaveResult;
}

const shouldShowCommunityDropdown = (moduleType: Module): boolean =>
  [Module.Admin, Module.PopHub, Module.Questionnaire].includes(moduleType);

const getTitle = (id?: string): string =>
  StringHelper.whenNullOrEmpty(id, Constants.emptyGuid) === Constants.emptyGuid
    ? "New Setting"
    : "Edit Setting";

const convertToDropdownOptions = (
  communities: CommunityDto[]
): DropdownOption[] => {
  return communities.map((community) => ({
    key: community.id,
    value: community.id,
    display: community.name ?? Constants.emptyString,
  }));
};

const getValidationSchema = (showCommunityDropdown: boolean) =>
  yup.object().shape({
    name: yup.string().required("Name is required"),
    description: yup.string(),
    value: yup.string().required("Value is required"),
    communityId: yup.string().when([], {
      is: () => showCommunityDropdown,
      then: (schema) => schema.required("Community is required"),
      otherwise: (schema) => schema.optional(),
    }),
  });

export const SettingForm = <T extends SettingTypeDto>(
  props: SettingFormProps<T>
) => {
  const showCommunityDropdown: boolean = shouldShowCommunityDropdown(
    props.moduleType
  );
  const title: string = getTitle(props.data?.id);
  const validationSchema = getValidationSchema(showCommunityDropdown);

  const handleSubmit = (values: T) => {
    return Promise.resolve(props.onSave(values)).then((result) => {
        if (hasClientErrorOperationStatusCode(result)) {
            return;
        }

        props.onHide();
    });
  };

  return (
    <Drawer.Form
      className="w-30"
      show={props.show}
      backdrop
      placement="end"
      scroll
      enableReinitialize
      title={title}
      initialValues={props.data ?? {}}
      validationSchema={validationSchema}
      onHide={props.onHide}
      on_Submit={handleSubmit}
    >
      <Form.Text
        name="name"
        placeholder="Enter text"
        label="Name"
        isLoading={props.loading}
      />
      <Form.TextArea
        name="description"
        rows={3}
        label="Description"
        isLoading={props.loading}
      />
      <Form.Text
        name="value"
        placeholder="Enter Value"
        label="Value"
        isLoading={props.loading}
      />
    </Drawer.Form>
  );
};
